<script lang="ts">
  import { getContext } from "svelte"
  import { slide } from "svelte/transition"
  import Dropdown from "$app/components/Dropdown.svelte"
  import {
    Home,
    SquareMenu,
    Printer,
    ChevronDown,
    ChevronRight,
    CreditCard,
    Cog,
    Repeat2,
    LifeBuoy,
    CalendarCheck2,
    Target,
    Flame
  } from "lucide-svelte"
  import { appStore } from "$app/store.svelte"

  interface Props {
    plans: any[]
    plan: any
    plan_role?: string
  }

  let { plans, plan, plan_role = null }: Props = $props()

  const hostname = location.hostname.substring(
    0,
    location.hostname.indexOf(".")
  )

  let role = ""
  if (appStore?.current_user?.role) {
    role = appStore.current_user.role
  } else if (plan_role) {
    role = plan_role
  }

  let canAdminPlan = ["coach", "admin", "leader"].includes(role)
  let canAdminBilling = ["admin"].includes(role)
  let canViewMenu = ["coach", "admin", "leader", "manager", "member"].includes(
    role
  )

  let otherPlans = $derived(plans.filter((p) => p.id !== plan.id))

  let openDropdown = $state(false)
  let showPlans = $state(false)
</script>

{#if plan?.id && canViewMenu}
  <Dropdown bind:open={openDropdown}>
    {#snippet button()}
      <span data-name="nav-button" class="flex items-center justify-center">
        <img
          src={plan.logo_url}
          class="inline-block h-10 w-10 rounded-full hover:border-indigo-300 hover:border hover:shadow bg-white p-1"
          alt={plan.name}
        />
      </span>
    {/snippet}
    <div class="relative">
      <div
        class="origin-top-right absolute right-0 mt-1 w-72 rounded-md shadow-lg py-1 bg-core border z-50"
      >
        <div
          class="group flex items-center justify-center px-4 py-3 border-b border-gray-400 bg-gray-100"
        >
          <img class="h-6 w-6 rounded-full mr-2" src={plan.logo_url} alt="" />
          <p class="text-sm font-bold text-black">
            {plan.name}
          </p>
        </div>

        {#if plans.length > 1 || appStore.current_user.coach}
          <a
            href="/plans"
            class="group flex items-center px-4 py-3 text-sm text-gray-700 border-b border-gray-300 hover:bg-gray-100 hover:text-gray-900"
            role="menuitem"
            aria-label="Plan List"
          >
            <SquareMenu
              class="h-5 w-5 mr-3 text-gray-400 group-hover:text-gray-500"
            />
            View Plan List
          </a>
        {:else if plans.length == 1}
          <a
            href="/plans/{plans[0].id}"
            class="group flex items-center px-4 py-3 text-sm text-gray-700 border-b border-gray-300 hover:bg-gray-100 hover:text-gray-900"
            role="menuitem"
            aria-label="My Plan"
          >
            <Home
              class="h-5 w-5 mr-3 text-gray-400 group-hover:text-gray-500"
            />
            {plans[0].name} Home
          </a>
        {/if}
        {#if hostname !== "teams" && (canAdminBilling || appStore.current_user?.admin)}
          <a
            href={`/plans/${plan.id}/admin`}
            class="group flex items-center px-4 py-3 text-sm text-gray-700 border-b border-gray-300 hover:bg-gray-100 hover:text-gray-900"
            role="menuitem"
            data-name="plan-billing-link"
          >
            <CreditCard
              class="h-5 w-5 mr-3 text-gray-400 group-hover:text-gray-500"
            />
            Billing and Subscription
          </a>
        {/if}
        {#if canAdminPlan}
          <a
            href={`/plans/${plan.id}/settings`}
            class="group flex items-center px-4 py-3 text-sm text-gray-700 border-b border-gray-300 hover:bg-gray-100 hover:text-gray-900"
            role="menuitem"
            data-name="plan-settings-link"
          >
            <Cog class="h-5 w-5 mr-3 text-gray-400 group-hover:text-gray-500" />
            Plan Settings
          </a>
          <a
            href={`/plans/${plan.id}/meetings`}
            class="group flex items-center px-4 py-3 text-sm text-gray-700 border-b border-gray-300 hover:bg-gray-100 hover:text-gray-900"
            role="menuitem"
            data-name="plan-meetings-link"
          >
            <CalendarCheck2
              class="h-5 w-5 mr-3 text-gray-400 group-hover:text-gray-500"
            />
            Transition Meetings
          </a>
          <a
            href={`/plans/${plan.id}/alignment_tests`}
            class="group flex items-center px-4 py-3 text-sm text-gray-700 border-b border-gray-300 hover:bg-gray-100 hover:text-gray-900"
            role="menuitem"
            data-name="alignment-tests-link"
          >
            <Target
              class="h-5 w-5 mr-3 text-gray-400 group-hover:text-gray-500"
            />
            Alignment Tests
          </a>
        {/if}
        <a
          class="group flex items-center px-4 py-3 text-sm text-gray-700 border-b border-gray-300 hover:bg-gray-100 hover:text-gray-900"
          href="/plans/{plan.id}/accomplishments"
          role="menuitem"
        >
          <Flame
            class="h-5 w-5 mr-3  text-gray-400 group-hover:text-gray-500"
          />
          Accomplishments
        </a>
        <a
          href={`/plans/${plan.id}.pdf`}
          class="group flex items-center px-4 py-3 text-sm text-gray-700 border-b border-gray-300 hover:bg-gray-100 hover:text-gray-900"
          rel="nofollow noopener noreferrer"
          target="_blank"
        >
          <Printer
            class="h-5 w-5 mr-3 text-gray-400 group-hover:text-gray-500"
          />
          Print One Page View
        </a>
        <a
          href={`/resources?plan_id=${plan.id}`}
          class="group flex items-center px-4 py-3 text-sm text-gray-700 border-b border-gray-300 hover:bg-gray-100 hover:text-gray-900"
          role="menuitem"
          data-name="resources-link"
        >
          <LifeBuoy
            class="h-5 w-5 mr-3 text-gray-400 group-hover:text-gray-500"
          />
          Resource Center
        </a>

        <a
          href={`/plans/${plan.id}/flywheel`}
          class="group flex items-center px-4 py-3 text-sm text-gray-700 border-b border-gray-300 hover:bg-gray-100 hover:text-gray-900"
          role="menuitem"
        >
          <Repeat2
            class="h-5 w-5 mr-3 text-gray-400 group-hover:text-gray-500"
          />
          FlyWheel
        </a>

        {#if otherPlans.length > 0}
          <div>
            <button
              onclick={(e) => {
                e.stopPropagation()
                showPlans = !showPlans
              }}
              class="bg-blue-200 hover:bg-blue-300 text-blue-500 text-center p-2 text-xs font-bold flex justify-center items-center w-full cursor-pointer"
            >
              Show Other Plans

              {#if showPlans}
                <ChevronDown class="inline-flex" />
              {:else}
                <ChevronRight class="inline-flex" />
              {/if}
            </button>
            {#if showPlans}
              <div
                transition:slide
                class="border-b-4 border-blue-200 bg-white transition-transform ease-out origin-top transform"
              >
                {#each otherPlans as plan}
                  <div
                    class="flex justify-start items-center hover:bg-gray-100 opacity-60 border-b border-gray-200"
                  >
                    <a
                      href={`/plans/${plan.id}`}
                      class="group flex items-center px-4 py-2 hover:text-gray-900"
                      role="menuitem"
                    >
                      <img
                        class="shadow h-6 w-6 rounded-full mr-2"
                        src={plan.logo_url}
                        alt=""
                      />
                      <div>
                        <p class="text-sm text-gray-800">
                          {plan.name}
                        </p>
                      </div>
                    </a>
                  </div>
                {/each}
              </div>
            {/if}
          </div>
        {/if}
      </div>
    </div>
  </Dropdown>
{:else if plans.length > 1 || appStore.current_user.role === "coach"}
  <a
    href="/plans"
    class="group flex items-center px-3 py-2 text-sm text-white border rounded hover:border-indigo-500 bg-indigo-700 border-indigo-700 hover:bg-indigo-800"
    role="menuitem"
    aria-label="Plan List"
  >
    <SquareMenu class="h-6 w-6 mr-2 text-white" />
    Your Plans
  </a>
{:else if plans.length == 1}
  <a
    href="/plans/{plans[0].id}"
    class="group flex items-center px-3 py-2 text-sm text-white border rounded hover:border-indigo-500 bg-indigo-700 border-indigo-700 hover:bg-indigo-800"
    role="menuitem"
    aria-label="My Plan"
  >
    <Home class="h-5 w-5 mr-3 text-white" />
    {plans[0].name} Home
  </a>
{/if}
